import { PRIORITIES, type Label, type Member } from '@hive/shared';
import { ChartGantt, Columns3, List, Search } from 'lucide-react';
import { forwardRef } from 'react';
import { Input, Select } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { hasFilters, NO_FILTERS, type DueFilter, type GridFilters, type SortKey } from './model';

export type GridView = 'list' | 'board' | 'gantt';

const SORTS: { id: SortKey; name: string }[] = [
  { id: 'manual', name: 'Manual order' },
  { id: 'due', name: 'Due date' },
  { id: 'priority', name: 'Priority' },
  { id: 'newest', name: 'Newest' },
  { id: 'title', name: 'Title' },
];

const DUES: { id: DueFilter; name: string }[] = [
  { id: 'any', name: 'Any due date' },
  { id: 'overdue', name: 'Overdue' },
  { id: 'today', name: 'Due today' },
  { id: 'week', name: 'Due within a week' },
  { id: 'none', name: 'No due date' },
];

export const GridToolbar = forwardRef<
  HTMLInputElement,
  {
    view: GridView;
    onView: (view: GridView) => void;
    filters: GridFilters;
    onFilters: (filters: GridFilters) => void;
    sort: SortKey;
    onSort: (sort: SortKey) => void;
    members: readonly Member[];
    labels: readonly Label[];
    /** The game's sprints, newest first. */
    sprints?: readonly { id: string; name: string; closed_at: string | null }[];
  }
>(function GridToolbar({ view, onView, filters, onFilters, sort, onSort, members, labels, sprints = [] }, searchRef) {
  const set = (patch: Partial<GridFilters>) => onFilters({ ...filters, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex" role="group" aria-label="View">
        {(
          [
            ['list', 'List', List],
            ['board', 'Board', Columns3],
            ['gantt', 'Gantt', ChartGantt],
          ] as const
        ).map(([id, name, Icon]) => (
          <button
            key={id}
            type="button"
            aria-pressed={view === id}
            onClick={() => onView(id)}
            className={cn(
              'flex cursor-pointer items-center gap-1.5 px-3 py-1.5 font-display text-[10px] font-bold tracking-[0.14em] uppercase shadow-[inset_0_0_0_1px_var(--color-line-hot)]',
              view === id
                ? 'bg-cyan/15 text-cyan shadow-[inset_0_0_0_1px_var(--color-cyan)]'
                : 'text-muted hover:text-text',
            )}
          >
            <Icon className="size-3.5" aria-hidden />
            {name}
          </button>
        ))}
      </div>
      <label className="relative flex min-w-48 flex-1 items-center">
        <Search className="pointer-events-none absolute left-2 size-3.5 text-muted" aria-hidden />
        <Input
          ref={searchRef}
          type="search"
          value={filters.text}
          onChange={(e) => set({ text: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && filters.text) {
              e.stopPropagation();
              set({ text: '' });
            }
          }}
          placeholder="Search tasks (press /)"
          aria-label="Search tasks"
          className="pl-7"
        />
      </label>
      <Select
        aria-label="Assignee"
        className="w-auto"
        value={filters.assignee ?? ''}
        onChange={(e) => set({ assignee: e.target.value || null })}
      >
        <option value="">Anyone</option>
        <option value="unassigned">Unassigned</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.display_name}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Label"
        className="w-auto"
        value={filters.labelId ?? ''}
        onChange={(e) => set({ labelId: e.target.value || null })}
      >
        <option value="">Any label</option>
        {labels.map((l) => (
          <option key={l.id} value={l.id}>
            #{l.name}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Priority"
        className="w-auto"
        value={filters.priority ?? ''}
        onChange={(e) => set({ priority: e.target.value ? Number(e.target.value) : null })}
      >
        <option value="">Any priority</option>
        {PRIORITIES.map((p) => (
          <option key={p.id} value={p.id}>
            P{p.id} {p.name}
          </option>
        ))}
      </Select>
      {sprints.length ? (
        <Select
          aria-label="Sprint"
          className="w-auto"
          value={filters.sprint ?? ''}
          onChange={(e) => set({ sprint: e.target.value || null })}
        >
          <option value="">Any sprint</option>
          <option value="current">Current sprint</option>
          <option value="backlog">Backlog (no sprint)</option>
          {sprints.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.closed_at ? ' (closed)' : ''}
            </option>
          ))}
        </Select>
      ) : null}
      <Select
        aria-label="Due"
        className="w-auto"
        value={filters.due}
        onChange={(e) => set({ due: e.target.value as DueFilter })}
      >
        {DUES.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </Select>
      <Select aria-label="Sort" className="w-auto" value={sort} onChange={(e) => onSort(e.target.value as SortKey)}>
        {SORTS.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </Select>
      {hasFilters(filters) ? (
        <button
          type="button"
          className="cursor-pointer font-mono text-xs text-cyan hover:underline"
          onClick={() => onFilters(NO_FILTERS)}
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
});
